import { type Sentiment } from '@/lib/types';
import { cn } from '@/lib/cn';
import { SentimentSkeleton } from './Skeleton';

interface SentimentDisplayProps {
  sentiment: Sentiment | null;
  isLoading?: boolean;
}

export default function SentimentDisplay({ sentiment, isLoading }: SentimentDisplayProps) {
  if (isLoading) {
    return <SentimentSkeleton />;
  }

  if (!sentiment) {
    return null;
  }

  const emotionConfig = {
    positive: {
      label: 'Positive',
      color: 'bg-green-100 text-green-800 border-green-200',
      icon: '😊',
    },
    neutral: {
      label: 'Neutral',
      color: 'bg-gray-100 text-gray-800 border-gray-200',
      icon: '😐',
    },
    negative: {
      label: 'Negative',
      color: 'bg-orange-100 text-orange-800 border-orange-200',
      icon: '😕',
    },
    angry: {
      label: 'Angry',
      color: 'bg-red-100 text-red-800 border-red-200',
      icon: '😡',
    },
    urgent: {
      label: 'Urgent',
      color: 'bg-purple-100 text-purple-800 border-purple-200',
      icon: '⚡',
    },
    automated: {
      label: 'Automated',
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      icon: '🤖',
    },
  };

  const config = emotionConfig[sentiment.emotion as keyof typeof emotionConfig];
  const scorePercent = Math.round(sentiment.score * 100);
  const confidencePercent = Math.round(sentiment.confidence * 100);

  return (
    <div className="rounded-lg border p-4">
      <h3 className="font-medium mb-3">Sentiment Analysis</h3>

      {/* Emotion Badge */}
      <div className="flex items-center gap-2 mb-3">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border',
            config.color
          )}
        >
          <span>{config.icon}</span>
          <span>{config.label}</span>
        </span>
        <span className="text-xs text-muted-foreground">
          {confidencePercent}% confidence
        </span>
      </div>

      {/* Score Bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-muted-foreground">Positivity Score</span>
          <span className="font-medium">{scorePercent}%</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full transition-all',
              sentiment.score > 0.6
                ? 'bg-green-500'
                : sentiment.score > 0.4
                  ? 'bg-gray-500'
                  : 'bg-red-500'
            )}
            style={{ width: `${scorePercent}%` }}
          />
        </div>
      </div>

      {/* Summary */}
      <p className="text-sm text-muted-foreground">{sentiment.summary}</p>
    </div>
  );
}
